import { useEffect, useState } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { ssupClaims, type SsupClaims } from "@storyworm/schema";
import { isSsupAdmin } from "@storyworm/ssup-data";
import { auth, db } from "../services/firebase.service";

/**
 * The signed-in session for the SSUP partner validation portal.
 *
 * Two distinct sign-in paths resolve to the same session shape against the
 * `storyworm-ssup` project:
 *  - Admins (StoryWorm staff): Google sign-in. Admin status is the presence of
 *    an `admins/{email}` doc (maintained by the console) — no custom claim, no
 *    Cloud Function; 2FA is handled by Google Workspace.
 *  - Partners (external licensees): email/password, provisioned by the console,
 *    which stamps their `role`/`org_id` claim directly via the Admin SDK.
 *
 * This hook owns only the *identity* resolution (who is signed in and what may
 * they see); sign-in/out *actions* live in {@link useSsupAuthActions}.
 */
export interface SsupSession {
  user: User | null;
  /** Partner role/org from the JWT claim, validated against the shared contract. */
  claims: SsupClaims | null;
  /** StoryWorm-staff admin, per the `admins/{email}` allowlist. */
  isAdmin: boolean;
  /** False until the first auth-state callback has resolved. */
  ready: boolean;
}

const SIGNED_OUT: SsupSession = { user: null, claims: null, isAdmin: false, ready: true };

export function useSsupSession(): SsupSession {
  const [session, setSession] = useState<SsupSession>({ ...SIGNED_OUT, ready: false });

  useEffect(() => {
    return onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setSession(SIGNED_OUT);
        return;
      }
      // Resolve claim + admin status together so the UI never flickers through a
      // partially-resolved state.
      const [claims, isAdmin] = await Promise.all([resolveClaims(user), resolveAdmin(user)]);
      setSession({ user, claims, isAdmin, ready: true });
    });
  }, []);

  return session;
}

async function resolveClaims(user: User): Promise<SsupClaims | null> {
  try {
    const { claims } = await user.getIdTokenResult();
    const parsed = ssupClaims.safeParse(claims);
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

async function resolveAdmin(user: User): Promise<boolean> {
  const email = user.email?.toLowerCase();
  if (!email) return false;
  try {
    return await isSsupAdmin(db, email);
  } catch {
    return false;
  }
}
